import { customElement } from 'aurelia';
import template from './confirm-dialog.html';

type Resolver = (value: boolean) => void;

export type DuplicateResultStatus = 'rejected' | 'added';

export type DuplicateResultItem = {
  fileName: string;
  status: DuplicateResultStatus;
  statusText: string;
  detail: string;
};

@customElement({ name: 'confirm-dialog', template })
export class ConfirmDialog {
  public visible = false;
  public title = '';
  public message = '';
  public isAlert = false;
  public isResultList = false;
  public resultItems: DuplicateResultItem[] = [];
  public isTextConfirm = false;
  public textValue = '';
  public textLabel = '';
  public textPlaceholder = '';
  public textError = '';

  private _resolve?: Resolver;
  private _expectedText = '';
  private _mismatchMessage = '';
  private static _instance?: ConfirmDialog;

  public attached(): void {
    ConfirmDialog._instance = this;
  }

  public detached(): void {
    if (ConfirmDialog._instance === this) {
      ConfirmDialog._instance = undefined;
    }
  }

  public static confirm(message: string, title = ''): Promise<boolean> {
    if (!ConfirmDialog._instance) return Promise.resolve(window.confirm(message));
    return ConfirmDialog._instance._show(message, title, false);
  }

  public static alert(message: string, title = ''): Promise<void> {
    if (!ConfirmDialog._instance) { window.alert(message); return Promise.resolve(); }
    return ConfirmDialog._instance._show(message, title, true).then(() => undefined);
  }

  public static alertDuplicateResults(
    items: DuplicateResultItem[],
    title = '',
    message = '',
  ): Promise<void> {
    if (!ConfirmDialog._instance) {
      const lines = items.map(item =>
        `${item.statusText}: ${item.fileName}${item.detail ? `\n  ${item.detail}` : ''}`);
      window.alert([message, ...lines].filter(Boolean).join('\n\n'));
      return Promise.resolve();
    }
    return ConfirmDialog._instance._showResultList(message, title, items).then(() => undefined);
  }

  public static confirmText(
    message: string,
    expectedText: string,
    title = '',
    textLabel = '',
    textPlaceholder = '',
    mismatchMessage = '',
  ): Promise<boolean> {
    if (!ConfirmDialog._instance) {
      const value = window.prompt(message, '') ?? '';
      return Promise.resolve(value.trim() === expectedText);
    }
    return ConfirmDialog._instance._showText(
      message,
      expectedText,
      title,
      textLabel,
      textPlaceholder,
      mismatchMessage,
    );
  }

  private _show(message: string, title: string, isAlert: boolean): Promise<boolean> {
    this.message = message;
    this.title = title;
    this.isAlert = isAlert;
    this.isResultList = false;
    this.resultItems = [];
    this.isTextConfirm = false;
    this.textValue = '';
    this.textLabel = '';
    this.textPlaceholder = '';
    this.textError = '';
    this._expectedText = '';
    this._mismatchMessage = '';
    this.visible = true;
    return new Promise(resolve => { this._resolve = resolve; });
  }

  private _showText(
    message: string,
    expectedText: string,
    title: string,
    textLabel: string,
    textPlaceholder: string,
    mismatchMessage: string,
  ): Promise<boolean> {
    this.message = message;
    this.title = title;
    this.isAlert = false;
    this.isResultList = false;
    this.resultItems = [];
    this.isTextConfirm = true;
    this.textValue = '';
    this.textLabel = textLabel;
    this.textPlaceholder = textPlaceholder;
    this.textError = '';
    this._expectedText = expectedText;
    this._mismatchMessage = mismatchMessage;
    this.visible = true;
    return new Promise(resolve => { this._resolve = resolve; });
  }

  private _showResultList(
    message: string,
    title: string,
    items: DuplicateResultItem[],
  ): Promise<boolean> {
    this.message = message;
    this.title = title;
    this.isAlert = true;
    this.isResultList = true;
    this.resultItems = items;
    this.isTextConfirm = false;
    this.textValue = '';
    this.textLabel = '';
    this.textPlaceholder = '';
    this.textError = '';
    this._expectedText = '';
    this._mismatchMessage = '';
    this.visible = true;
    return new Promise(resolve => { this._resolve = resolve; });
  }

  public resultIconClass(item: DuplicateResultItem): string {
    return item.status === 'rejected' ? 'fa-circle-xmark' : 'fa-circle-check';
  }

  public resultStatusClass(item: DuplicateResultItem): string {
    return item.status === 'rejected' ? 'rejected' : 'added';
  }

  public ok(): void {
    if (this.isTextConfirm && this.textValue.trim() !== this._expectedText) {
      this.textError = this._mismatchMessage || 'The entered value does not match.';
      return;
    }

    this.visible = false;
    this._resolve?.(true);
    this._resolve = undefined;
  }

  public cancel(): void {
    this.visible = false;
    this._resolve?.(false);
    this._resolve = undefined;
  }
}
